import React from "react";
import styled from "@emotion/styled";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import { matchSorter } from "match-sorter";
import { debounce } from "throttle-debounce";

//all this styling stuff with emotion is in here to support the example styling/behavior for Downshift. This should probably be cleaned up, but Keen didn't have something good to use oob
const Item = styled("li")(
  {
    position: "relative",
    cursor: "pointer",
    display: "block",
    border: "none",
    height: "auto",
    textAlign: "left",
    borderTop: "none",
    fontSize: "0.875rem",
    lineHeight: "1.5",
    textTransform: "none",
    boxShadow: "none",
    padding: "0px 15px 1px",
    whiteSpace: "normal",
    wordWrap: "normal",
  },
  (props: any) => ({
    color:
      props.isActive || props.isSelected
        ? "rgba(0,0,0,.95)"
        : "rgba(0,0,0,.87)",
    background: props.isActive ? "rgba(0,0,0,.03)" : null,
    fontWeight: props.isSelected ? 700 : 400,
  })
);
const onAttention = "&:hover, &:focus";

const Input = styled("input")(
  {
    display: "inline-block",
    position: "relative",
    padding: ".65rem 3rem .65rem 1rem",
    lineHeight: 1.5,
    [onAttention]: {
      borderColor: "#96c8da",
      boxShadow: "0 2px 3px 0 rgba(34,36,38,.15)",
    },
  },
  (props: any) =>
    props.isOpen
      ? {
          [onAttention]: {
            boxShadow: "none",
          },
        }
      : null
);

const BaseMenu = styled("ul")(
  {
    display: "block",
    padding: 0,
    marginTop: 0,
    position: "absolute",
    backgroundColor: "white",
    width: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    outline: "0",
    transition: "opacity .1s ease",
    borderRadius: "0 0 .28571429rem .28571429rem",
    boxShadow: "0 2px 3px 0 rgba(34,36,38,.15)",
    borderColor: "#96c8da",
    borderTopWidth: "0",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderStyle: "solid",
    zIndex: 1051,
    maxHeight: 200
  },
  (props: any) => ({
    border: props.isOpen ? null : "none",
  })
);

const Menu = React.forwardRef((props, ref) => (
  <BaseMenu ref={ref} {...props} />
));

const ControllerButton = styled("span")({
  fontFamily: "LineAwesome",
  textDecoration: "inherit",
  textRendering: "optimizeLegibility",
  textTransform: "none",
  fontSmoothing: "antialiased",
  border: 0,
  top: 0,
  position: "absolute",
  borderWidth: 0,
  background: null,
  marginTop: 0,
  marginLeft: 0,
  fontSize: ".85rem",
  left: "auto",
  right: 0,
  display: "inline-block",
  width: "1.9rem",
  appearance: "inherit",
});

type IProps = {
  className: string;
  initialSelectedItem: any;
  items: Array<any>;
  itemKey: (item: any) => any;
  itemToString: (item: any) => string;
  onChange: (selection: any) => void;
  matchKeys: Array<string>;
} & Partial<IOptionalProps>;

interface IOptionalProps {
  selectedItem: any;
  required: boolean;
  disabled: boolean;
  name: string;
  maxItems: number;
  debounceInterval: number;
  width: string;
  placeholder: string;
  minCharactersForSearch: number;
  filter: (filter: string) => any[] | Promise<any[]>;
}

interface IState {
  results: any[];
  isLoading: boolean;
  inputValue: string;
}

class Autocomplete extends React.Component<IProps, IState> {
  private autocompleteSearchDebounced: (
    inputValue: string,
    selectedItem: any
  ) => any[];
  private defaultDebounceInterval = 500;
  private defaultMinCharactersForSearch = 1;
  
  constructor(props: IProps) {
    super(props);
    this.state = {
      results: null,
      isLoading: false,
      inputValue: props.itemToString(props.selectedItem),
    };

    this.autocompleteSearchDebounced = debounce(
      this.props.debounceInterval ?? this.defaultDebounceInterval,
      false,
      (inputValue: string, selectedItem: any) =>
        this.getItems(inputValue, selectedItem)
    );
  }

  getItems(filter: string, selectedItem: any): any[] {
    // clear the drop down if there is no filter string provided
    if (!filter || !filter.trim()) {
      this.setState({ results: null, isLoading: false });
      return;
    }

    // if the selected item is the same as the filter string do not make the call again.
    if (this.props.itemToString(selectedItem) == filter) {
      this.setState({ isLoading: false });
      return;
    }

    var items: any[] | Promise<Array<any>>;
    items = this.props.filter
      ? this.props.filter(filter)
      : matchSorter(this.props.items, filter, { keys: this.props.matchKeys });

    Promise.resolve(items).then((results) => {
      if (this.props.maxItems) {
        results.splice(this.props.maxItems);
      }
      this.setState({ results: results, isLoading: false });
    });
  }

  onChange(
    selectedItem: any,
    stateAndHelpers: ControllerStateAndHelpers<any>
  ): void {
    this.props.onChange(selectedItem);

    var inputId = stateAndHelpers.getInputProps().id;
    var element = document.getElementById(inputId) as HTMLInputElement;
    if (this.props.required && !selectedItem) {
      element.setCustomValidity("Please select an item from the list");
    } else {
      element.setCustomValidity("");
    }
  }

  render() {
    return (
      <Downshift
        onChange={(e, s) => this.onChange(e, s)}
        onInputValueChange={(inputValue: string, { selectedItem }) => {
          if (inputValue && inputValue != this.state.inputValue) {
            this.setState({ isLoading: true, inputValue: inputValue });
          }
          this.autocompleteSearchDebounced(inputValue, selectedItem);
        }}
        itemToString={this.props.itemToString}
        initialSelectedItem={this.props.initialSelectedItem}
        selectedItem={this.props.selectedItem}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          clearSelection,
          highlightedIndex,
          selectedItem,
        }: any) => (
          <div
            data-toggle="tooltip"
            title={this.props.itemToString(selectedItem)}
            style={{ width: this.props.width ?? null }}
          >
            <span style={{ position: "relative" }}>
              <Input
                className={this.props.className}
                required={this.props.required}
                disabled={this.props.disabled}
                name={this.props.name}
                placeholder={this.props.placeholder}
                {...getInputProps({
                  isOpen,
                })}
              />
              {selectedItem ? (
                <ControllerButton
                  onClick={() => {
                    clearSelection();
                  }}
                  aria-label="clear selection"
                >
                  <em className="la la-close" />
                </ControllerButton>
              ) : null}
            </span>
            <div style={{ position: "relative" }}>
              <Menu 
                {...getMenuProps({
                  isOpen,
                })}
              >
                {isOpen && this.state.results ? (
                  this.state.results.map((item, index) => (
                    <Item
                      key={this.props.itemKey(item)}
                      {...getItemProps({
                        index,
                        item,
                        isActive: highlightedIndex === index,
                        isSelected: selectedItem === item,
                      })}
                    >
                      {this.props.itemToString(item)}
                    </Item>
                  ))
                ) : this.state.isLoading ? (
                  <Item key={0}>
                    {this.state.inputValue.length >= (this.props.minCharactersForSearch ?? this.defaultMinCharactersForSearch)
                      ? "Loading..."
                      : "Type more characters to search..."}
                  </Item>
                ) : null}
              </Menu>
            </div>
          </div>
        )}
      </Downshift>
    );
  }
}

export default Autocomplete;
